import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getDashboardByMember, formatCurrency, MONTHS } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, RefreshCw, Users as UsersIcon, Home as HomeIcon,
  TrendingUp, TrendingDown, PiggyBank, CreditCard, Wallet, Sparkles,
} from 'lucide-react';

const colorFromName = (name) => {
  const palette = [
    'bg-emerald-500', 'bg-blue-500', 'bg-amber-500', 'bg-purple-500',
    'bg-rose-500', 'bg-cyan-500', 'bg-orange-500', 'bg-indigo-500',
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i)) % palette.length;
  return palette[h];
};
const initials = (name) =>
  name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const TypeRow = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center justify-between py-1.5 text-sm border-b last:border-0">
    <span className="inline-flex items-center gap-2 text-muted-foreground">
      <Icon className={`w-3.5 h-3.5 ${accent}`} strokeWidth={1.75} /> {label}
    </span>
    <span className={`tabular-nums font-semibold ${accent}`}>{formatCurrency(value)}</span>
  </div>
);

const MemberCard = ({ m, isSelf, isMax }) => (
  <Card className={`relative overflow-hidden ${isSelf ? 'border-primary/40' : ''}`} data-testid={`member-card-${m.user_id}`}>
    {isMax && (
      <div className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
        <Sparkles className="w-2.5 h-2.5" /> top contributor
      </div>
    )}
    <CardHeader className="pb-2">
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-full ${colorFromName(m.name)} text-white flex items-center justify-center font-bold shrink-0`}>
          {initials(m.name)}
        </div>
        <div className="min-w-0 flex-1">
          <CardTitle className="text-base flex items-center gap-2">
            {m.name}
            {isSelf && <Badge className="bg-primary/15 text-primary border-0 text-[9px]">tu</Badge>}
          </CardTitle>
          <p className="text-xs text-muted-foreground truncate">{m.email}</p>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-lg bg-muted/30 p-2.5">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Saldo</p>
          <p className={`text-xl font-bold tabular-nums ${m.balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
            {m.balance >= 0 ? '+' : ''}{formatCurrency(m.balance)}
          </p>
        </div>
        <div className="rounded-lg bg-muted/30 p-2.5">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Coinvolto in</p>
          <p className="text-xl font-bold tabular-nums">{m.transaction_count}</p>
          <p className="text-[10px] text-muted-foreground">tx del mese</p>
        </div>
      </div>
      <TypeRow icon={TrendingUp}   label="Reddito"  value={m.totals.REDDITO}  accent="text-emerald-600 dark:text-emerald-400" />
      <TypeRow icon={TrendingDown} label="Spese"    value={m.totals.SPESE}    accent="text-red-500" />
      <TypeRow icon={PiggyBank}    label="Risparmi" value={m.totals.RISPARMI} accent="text-blue-500" />
      <TypeRow icon={CreditCard}   label="Debiti"   value={m.totals.DEBITI}   accent="text-orange-500" />
    </CardContent>
  </Card>
);

const ByMemberPage = () => {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getDashboardByMember(month, year);
      setData(res.data);
    } catch (e) {
      toast.error('Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, year]);

  const changeMonth = (d) => {
    let m = month + d, y = year;
    if (m < 1) { m = 12; y--; } else if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y);
  };

  const maxIncomeMember = useMemo(() => {
    if (!data?.members?.length) return null;
    return data.members.reduce((max, m) => (m.totals.REDDITO > (max?.totals?.REDDITO || 0) ? m : max), null);
  }, [data]);

  if (loading || !data) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-72" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => <div key={i} className="h-64 bg-muted rounded-xl" />)}
        </div>
      </div>
    );
  }

  const totalAssigned = data.members.reduce((s, m) => s + m.transaction_count, 0);
  const hasUnassigned = data.unassigned.transaction_count > 0;

  return (
    <div className="space-y-6 animate-in" data-testid="by-member-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <UsersIcon className="w-7 h-7 text-primary" strokeWidth={1.5} />
            Per persona
          </h1>
          <p className="text-muted-foreground">
            Movimenti aggregati per intestatario · {data.total_transactions} transazioni · {totalAssigned} con intestatari
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} data-testid="bm-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="px-3 py-2 bg-card border rounded-lg min-w-[180px] text-center font-medium">
            {MONTHS[month - 1]} {year}
          </div>
          <Button variant="outline" size="icon" onClick={() => changeMonth(1)} data-testid="bm-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={load}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Members grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.members.map((m) => (
          <MemberCard
            key={m.user_id}
            m={m}
            isSelf={m.user_id === user?.id}
            isMax={maxIncomeMember && m.user_id === maxIncomeMember.user_id && m.totals.REDDITO > 0}
          />
        ))}
      </div>

      {/* Unassigned section */}
      {hasUnassigned && (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <HomeIcon className="w-4 h-4 text-muted-foreground" />
              Movimenti di tutta la famiglia (senza intestatari)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="rounded-lg bg-muted/30 p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Saldo</p>
                <p className={`text-lg font-bold tabular-nums ${data.unassigned.balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                  {data.unassigned.balance >= 0 ? '+' : ''}{formatCurrency(data.unassigned.balance)}
                </p>
              </div>
              <div className="rounded-lg bg-muted/30 p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Reddito</p>
                <p className="text-lg font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(data.unassigned.totals.REDDITO)}
                </p>
              </div>
              <div className="rounded-lg bg-muted/30 p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Spese</p>
                <p className="text-lg font-bold tabular-nums text-red-500">
                  {formatCurrency(data.unassigned.totals.SPESE)}
                </p>
              </div>
              <div className="rounded-lg bg-muted/30 p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Risparmi</p>
                <p className="text-lg font-bold tabular-nums text-blue-500">
                  {formatCurrency(data.unassigned.totals.RISPARMI)}
                </p>
              </div>
              <div className="rounded-lg bg-muted/30 p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Debiti</p>
                <p className="text-lg font-bold tabular-nums text-orange-500">
                  {formatCurrency(data.unassigned.totals.DEBITI)}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Sono i {data.unassigned.transaction_count} movimenti per cui non hai indicato un intestatario
              (es. spesa al supermercato, bolletta condominio). Per assegnarli, modifica la singola transazione.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Tip */}
      <Card className="bg-muted/30 border-dashed">
        <CardContent className="pt-5 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">Come funziona:</strong> quando una transazione ha più intestatari,
            l'importo viene <strong>diviso equamente</strong> nei totali per persona. Ad esempio, un affitto da
            €1.000 con 2 intestatari conta €500 per ciascuno.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ByMemberPage;
